import { useState } from "react";
import { useUser } from "../hooks/useAuthCompat";
import { useQuery } from "@tanstack/react-query";
import { useApi, publicApi } from "../hooks/useApi";
import { toast } from "sonner";

const TIER_SWITCH = {
  gold: { label: "Upgrade to Gold Pro", target: "gold_pro", monthlyKey: "gold_pro_monthly", annualKey: "gold_pro_annual", monthlyPrice: "$100/mo", annualPrice: "$960/yr" },
  gold_pro: { label: "Switch to Gold", target: "gold", monthlyKey: "gold_monthly", annualKey: "gold_annual", monthlyPrice: "$19/mo", annualPrice: "$182.40/yr" },
};

const PRODUCT_LABELS = { gold: "Gold", gold_pro: "Gold Pro", muscle_school: "The Muscle School", mods: "MODS" };

function ReceiptRow({ id, date, amount, summary, detailRows, isOpen, onToggle, onResend, resending, testIdPrefix }) {
  return (
    <div data-testid={`${testIdPrefix}-row-${id}`} className="py-4 text-sm">
      <div className="flex justify-between flex-wrap gap-2">
        <span className="text-white/60 text-xs">{date}</span>
        <span>{amount}</span>
      </div>
      <p className="mt-1">{summary}</p>
      <div className="flex items-center gap-4 mt-2">
        <button
          data-testid={`${testIdPrefix}-view-receipt-btn-${id}`}
          onClick={onToggle}
          className="text-xs uppercase tracking-wide text-white/60 hover:text-white underline underline-offset-2"
        >
          {isOpen ? "Hide Receipt" : "View Receipt"}
        </button>
        <button
          data-testid={`${testIdPrefix}-resend-receipt-btn-${id}`}
          onClick={onResend}
          disabled={resending}
          className="text-xs uppercase tracking-wide text-white/60 hover:text-white underline underline-offset-2 disabled:opacity-50"
        >
          {resending ? "Sending…" : "Resend Receipt"}
        </button>
      </div>
      {isOpen && (
        <div data-testid={`${testIdPrefix}-receipt-detail-${id}`} className="mt-3 border border-white/10 px-4 py-3 bg-white/[0.03]">
          <p className="text-xs text-white/50 mb-2">Order reference: {id.slice(0, 8)}</p>
          {detailRows}
          <div className="flex justify-between text-xs mt-2 pt-2 border-t border-white/10">
            <span className="text-white/50">Total charged</span>
            <span>{amount}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function friendlyDevice(ua) {
  if (!ua) return "Unknown device";
  const browser = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : "Browser";
  const os = /Windows/.test(ua) ? "Windows" : /Mac OS X/.test(ua) ? "macOS" : /Android/.test(ua) ? "Android" : /iPhone|iPad/.test(ua) ? "iOS" : /Linux/.test(ua) ? "Linux" : "an unknown device";
  return `${browser} on ${os}`;
}

export default function Account() {
  const { user, isSignedIn } = useUser();
  const api = useApi();
  const [switching, setSwitching] = useState(false);
  const [switchCycle, setSwitchCycle] = useState("monthly");
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [resendingOrder, setResendingOrder] = useState(null);
  const [revokingSession, setRevokingSession] = useState(null);
  const { data: orders } = useQuery({ queryKey: ["orders"], queryFn: async () => (await api.get("/account/orders")).data, enabled: !!isSignedIn });
  const { data: nativeOrders } = useQuery({ queryKey: ["native-orders"], queryFn: async () => (await api.get("/checkout/orders")).data, enabled: !!isSignedIn });
  const { data: membershipOrders } = useQuery({ queryKey: ["membership-orders"], queryFn: async () => (await api.get("/billing/orders")).data, enabled: !!isSignedIn });
  const { data: books } = useQuery({ queryKey: ["books"], queryFn: async () => (await publicApi.get("/books")).data });
  const { data: membership } = useQuery({ queryKey: ["gold-membership"], queryFn: async () => (await api.get("/gold/membership")).data, enabled: !!isSignedIn });
  const { data: sessionsData, refetch: refetchSessions } = useQuery({ queryKey: ["my-sessions"], queryFn: async () => (await api.get("/auth/sessions")).data, enabled: !!isSignedIn });
  const bookTitle = (id) => books?.find((b) => b.id === id)?.title;
  const hasStripeOrder = orders?.some((o) => o.source === "stripe_checkout");
  const tier = membership?.product_type;
  const switchOption = tier ? TIER_SWITCH[tier] : null;
  const switchOfferKey = switchOption ? (switchCycle === "monthly" ? switchOption.monthlyKey : switchOption.annualKey) : null;
  const switchPrice = switchOption ? (switchCycle === "monthly" ? switchOption.monthlyPrice : switchOption.annualPrice) : null;

  const manageBilling = async () => {
    try {
      const res = await api.post("/billing/portal", null, { params: { origin_url: window.location.origin } });
      window.location.href = res.data.portal_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No billing account found yet — subscribe first.");
    }
  };

  const switchTier = async () => {
    if (!switchOfferKey) return;
    setSwitching(true);
    try {
      const res = await api.post("/billing/checkout", { offer_key: switchOfferKey, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the switch.");
      setSwitching(false);
    }
  };

  const resendReceipt = async (orderId, endpoint) => {
    setResendingOrder(orderId);
    try {
      await api.post(`${endpoint}/${orderId}/resend-receipt`);
      toast.success("Receipt sent to your email.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resend the receipt.");
    } finally {
      setResendingOrder(null);
    }
  };

  const revokeSession = async (sessionIdMasked) => {
    const prefix = sessionIdMasked.replace("…", "");
    setRevokingSession(sessionIdMasked);
    try {
      await api.delete(`/auth/sessions/${prefix}`);
      toast.success("Signed that device out.");
      refetchSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not sign that session out.");
    } finally {
      setRevokingSession(null);
    }
  };

  if (!isSignedIn) return <div className="max-w-xl mx-auto px-6 py-24 text-center text-white/60" data-testid="account-signed-out">Sign in to manage your account.</div>;

  return (
    <div className="max-w-3xl mx-auto px-6 py-16" data-testid="account-page">
      <p className="text-xs uppercase tracking-[0.2em] text-textSecondary mb-2">Account</p>
      <div className="flex items-center justify-between gap-4 flex-wrap mb-8">
        <h1 className="font-display text-display-sm">{user?.fullName || user?.primaryEmailAddress?.emailAddress}</h1>
        {hasStripeOrder && (
          <button data-testid="account-manage-billing-btn" onClick={manageBilling} className="text-xs uppercase tracking-wide border border-white/20 px-4 py-2 hover:border-white/50 transition-colors">
            Manage Billing
          </button>
        )}
      </div>

      {tier && (
        <div data-testid="account-membership-section" className="flex items-center justify-between gap-4 flex-wrap border border-white/10 px-5 py-4 mb-10">
          <div>
            <p className="text-xs uppercase tracking-wide text-white/50 mb-1">Membership</p>
            <p data-testid="account-membership-tier" className="text-sm">{tier === "gold_pro" ? "Gold Pro" : "Gold"}</p>
          </div>
          {switchOption && (
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex border border-white/20" role="group" aria-label="Billing cycle">
                <button
                  data-testid="account-switch-cycle-monthly"
                  onClick={() => setSwitchCycle("monthly")}
                  className={`text-xs uppercase tracking-wide px-3 py-2 transition-colors ${switchCycle === "monthly" ? "bg-white text-black" : "text-white/60 hover:text-white"}`}
                >
                  Monthly
                </button>
                <button
                  data-testid="account-switch-cycle-annual"
                  onClick={() => setSwitchCycle("annual")}
                  className={`text-xs uppercase tracking-wide px-3 py-2 transition-colors border-l border-white/20 ${switchCycle === "annual" ? "bg-white text-black" : "text-white/60 hover:text-white"}`}
                >
                  Annual
                </button>
              </div>
              <button
                data-testid="account-switch-tier-btn"
                onClick={switchTier}
                disabled={switching}
                className="text-xs uppercase tracking-wide border border-white/20 px-4 py-2 hover:border-white/50 transition-colors disabled:opacity-50"
              >
                {switching ? "Redirecting…" : `${switchOption.label} · ${switchPrice}`}
              </button>
            </div>
          )}
        </div>
      )}

      <h2 className="text-sm uppercase tracking-wide text-white/70 mb-4 mt-10">Order History</h2>
      <div className="divide-y divide-white/10 border-t border-b border-white/10 mb-10" data-testid="order-history-section">
        {nativeOrders?.length ? nativeOrders.map((o) => (
          <ReceiptRow
            key={o.id}
            id={o.id}
            testIdPrefix="native-order"
            date={new Date(o.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
            amount={`$${(o.amount_subtotal_cents / 100).toFixed(2)}`}
            summary={o.items.map((i) => i.title).join(", ")}
            isOpen={expandedOrder === o.id}
            onToggle={() => setExpandedOrder(expandedOrder === o.id ? null : o.id)}
            onResend={() => resendReceipt(o.id, "/checkout/orders")}
            resending={resendingOrder === o.id}
            detailRows={o.items.map((i, idx) => (
              <div key={idx} className="flex justify-between text-xs text-white/80 py-0.5">
                <span>{i.title}</span>
                <span>${(i.unit_amount_cents / 100).toFixed(2)}</span>
              </div>
            ))}
          />
        )) : <p data-testid="order-history-empty" className="text-white/70 text-sm py-4">No book or bundle purchases yet.</p>}
      </div>

      <h2 className="text-sm uppercase tracking-wide text-white/70 mb-4">Membership History</h2>
      <div className="divide-y divide-white/10 border-t border-b border-white/10 mb-10" data-testid="membership-history-section">
        {membershipOrders?.length ? membershipOrders.map((o) => {
          const label = PRODUCT_LABELS[o.entitlement_product_type] || o.entitlement_product_type;
          const amount = `$${(o.amount_cents / 100).toFixed(2)}`;
          return (
            <ReceiptRow
              key={o.id}
              id={o.id}
              testIdPrefix="membership-order"
              date={new Date(o.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
              amount={amount}
              summary={label}
              isOpen={expandedOrder === o.id}
              onToggle={() => setExpandedOrder(expandedOrder === o.id ? null : o.id)}
              onResend={() => resendReceipt(o.id, "/billing/orders")}
              resending={resendingOrder === o.id}
              detailRows={(
                <div className="flex justify-between text-xs text-white/80 py-0.5">
                  <span>{label}</span>
                  <span>{amount}</span>
                </div>
              )}
            />
          );
        }) : <p data-testid="membership-history-empty" className="text-white/70 text-sm py-4">No membership purchases yet.</p>}
      </div>

      <h2 className="text-sm uppercase tracking-wide text-white/70 mb-4">Purchase & Entitlement History</h2>
      <div className="divide-y divide-white/10 border-t border-b border-white/10 mb-10">
        {orders?.length ? orders.map((o) => (
          <div key={o.id} data-testid={`order-row-${o.id}`} className="py-3 flex justify-between text-sm">
            <span>{o.product_type.replace("_", " ")} {o.product_id ? `— ${bookTitle(o.product_id) || o.product_id.slice(0, 8)}` : ""}</span>
            <span className="text-white/70">{o.status} · {o.source}</span>
          </div>
        )) : <p className="text-white/70 text-sm py-4">No purchases yet.</p>}
      </div>

      <h2 className="text-sm uppercase tracking-wide text-white/70 mb-4">Active Sessions</h2>
      <div className="divide-y divide-white/10 border-t border-b border-white/10" data-testid="account-sessions-section">
        {sessionsData?.sessions?.length ? sessionsData.sessions.map((s) => (
          <div key={s.session_id} data-testid={`session-row-${s.session_id}`} className="py-3 flex items-center justify-between gap-3 text-sm flex-wrap">
            <div>
              <span>{friendlyDevice(s.user_agent)}</span>
              {s.is_current && <span className="ml-2 text-xs uppercase tracking-wide text-white/50">(this device)</span>}
              <p className="text-white/50 text-xs mt-0.5">Signed in {new Date(s.created_at).toLocaleString()}{s.ip ? ` · ${s.ip}` : ""}</p>
            </div>
            {!s.is_current && (
              <button
                data-testid={`session-revoke-btn-${s.session_id}`}
                onClick={() => revokeSession(s.session_id)}
                disabled={revokingSession === s.session_id}
                className="text-xs uppercase tracking-wide border border-white/20 px-3 py-2 hover:border-white/50 transition-colors disabled:opacity-50"
              >
                {revokingSession === s.session_id ? "Signing out…" : "Sign Out"}
              </button>
            )}
          </div>
        )) : <p data-testid="account-sessions-empty" className="text-white/70 text-sm py-4">No active sessions.</p>}
      </div>
    </div>
  );
}
